import React, { useCallback, useId, useRef } from "react";
import { useIntl } from "react-intl";
import { ParagraphOutputFile } from "@/model/paragraph";
import { isValidParagraphUrl } from "@/util/helper/paragraphHelper";
import { useScenarioAutocompletes, useUploadedFiles, useSyncForm } from "@/hook";
import { TextInput, TextInputRef } from "@/component/input";

import { ParagraphInputsProps } from "../model";
import { ParagraphFileInput } from "../input";


const ParagraphOutputFileCardInputs: React.FC<ParagraphInputsProps<ParagraphOutputFile>> = (props) => {
    const { paragraph, onChange } = props;

    const intl = useIntl();
    const urlErrorId = useId();

    const urlInputRef = useRef<TextInputRef>(null);

    const { add, remove } = useUploadedFiles();
    const { variableAutocomplete } = useScenarioAutocompletes();

    const onUploadFile = (file: File, url: string) => {
        if (paragraph.data?.file_url) {
            remove(paragraph.data.file_url);
        }
        add(file, url);
        onChange({
            ...paragraph,
            data: {
                ...paragraph.data,
                file_url: url,
                file_size: file.size
            }
        });
        urlInputRef.current?.set(url);
    };

    const syncFromProps = useCallback(() => {
        if (!paragraph.data?.file_url || !urlInputRef.current) {
            return;
        }
        urlInputRef.current.set(paragraph.data?.file_url ?? "");
    }, [paragraph.data?.file_url]);

    useSyncForm(syncFromProps);

    const urlInvalid = !!paragraph.data?.file_url && !isValidParagraphUrl(paragraph.data?.file_url ?? "");

    return (
        <div className="col-span-2 flex flex-col gap-1.5">
            <div className="field-label">
                {intl.formatMessage({ id: "i04034" })}
            </div>
            <div className="w-full flex flex-col gap-1">
                {urlInvalid &&
                    <span id={urlErrorId}
                          className="text-invalid"
                    >
                        {intl.formatMessage({ id: "i04077" })}
                    </span>
                }
                <div className="flex gap-1 items-center">
                    <TextInput ref={urlInputRef}
                               required
                               invalid={!isValidParagraphUrl(paragraph.data?.file_url ?? "")}
                               aria-errormessage={urlInvalid ? urlErrorId : undefined}
                               defaultValue={paragraph.data?.file_url ?? ""}
                               placeholder={intl.formatMessage({ id: "i04035" })}
                               onChange={e => onChange({
                                   ...paragraph,
                                   data: { ...paragraph.data, file_url: e.target.value }
                               })}
                    />
                    <ParagraphFileInput extensionType="document"
                                        onLoad={onUploadFile}
                                        onError={onUploadFile}
                    />
                </div>

                <TextInput required
                           autocompletes={variableAutocomplete}
                           defaultValue={paragraph.data?.text ?? ""}
                           placeholder={intl.formatMessage({ id: "i04038" })}
                           onChange={e => onChange({
                               ...paragraph,
                               data: { ...paragraph.data, text: e.target.value }
                           })}
                />

                <input type="text"
                       className="input-default"
                       value={paragraph.data?.label ?? ""}
                       placeholder={intl.formatMessage({ id: "i04037" })}
                       aria-label={intl.formatMessage({ id: "i04037" })}
                       onChange={e => onChange({
                           ...paragraph,
                           data: {
                               ...paragraph.data,
                               label: e.target.value
                           }
                       })}
                />
            </div>
        </div>
    );
};

export default ParagraphOutputFileCardInputs;
